/**
 * ScatterPlot - Correlation visualization
 *
 * Usage:
 *   <ScatterPlot
 *     title="Sleep Quality vs Exercise"
 *     data={{
 *       points: [{ x: 45, y: 85, label: 'Mon' }, { x: 30, y: 72, label: 'Tue' }, ...],
 *       xLabel: 'Exercise (mins)',
 *       yLabel: 'Sleep Quality',
 *     }}
 *   />
 */

import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ZAxis,
} from 'recharts';
import type { BlockProps, ScatterPlotData } from './types';
import { CHART_COLORS } from './colors';
import './blocks.css';

export interface ScatterPlotProps extends BlockProps<ScatterPlotData> {
  /** Chart height in pixels */
  height?: number;
}

export function ScatterPlot({
  data,
  title,
  config,
  height = 280,
  className,
}: ScatterPlotProps) {
  const { points, xLabel, yLabel } = data;

  if (!points || points.length === 0) {
    return (
      <div className={`block-chart ${className || ''}`}>
        {title && <div className="block-chart-title">{title}</div>}
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-faint)' }}>
          No data available
        </div>
      </div>
    );
  }

  const dotColor = config?.colors?.[0] || CHART_COLORS[0];

  // Calculate correlation coefficient for display
  const calculateCorrelation = () => {
    const n = points.length;
    if (n < 2) return null;

    const sumX = points.reduce((sum, p) => sum + p.x, 0);
    const sumY = points.reduce((sum, p) => sum + p.y, 0);
    const sumXY = points.reduce((sum, p) => sum + p.x * p.y, 0);
    const sumX2 = points.reduce((sum, p) => sum + p.x * p.x, 0);
    const sumY2 = points.reduce((sum, p) => sum + p.y * p.y, 0);

    const numerator = n * sumXY - sumX * sumY;
    const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));

    if (denominator === 0) return null;
    return numerator / denominator;
  };

  const correlation = calculateCorrelation();

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;

    const point = payload[0].payload;

    return (
      <div style={{
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: '6px',
        padding: '8px 12px',
        boxShadow: 'var(--popover-shadow, 0 2px 4px rgba(0,0,0,0.1))',
      }}>
        {point.label && (
          <p style={{
            color: 'var(--color-text-subtle)',
            fontWeight: 600,
            margin: '0 0 4px 0',
            fontSize: '13px',
          }}>
            {point.label}
          </p>
        )}
        <p style={{ color: 'var(--color-text)', margin: '2px 0', fontSize: '13px' }}>
          {xLabel || 'X'}: {point.x.toLocaleString()}
        </p>
        <p style={{ color: 'var(--color-text)', margin: '2px 0', fontSize: '13px' }}>
          {yLabel || 'Y'}: {point.y.toLocaleString()}
        </p>
      </div>
    );
  };

  return (
    <div className={`block-chart ${className || ''}`}>
      {title && (
        <div className="block-chart-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{title}</span>
          {correlation !== null && (
            <span style={{
              fontSize: '11px',
              fontWeight: 500,
              color: correlation > 0.3 ? 'var(--color-success)' : correlation < -0.3 ? 'var(--color-danger)' : 'var(--color-text-muted)',
              background: 'var(--color-surface-muted)',
              padding: '2px 8px',
              borderRadius: '4px',
            }}>
              r = {correlation.toFixed(2)}
            </span>
          )}
        </div>
      )}

      <div className="block-chart-container">
        <ResponsiveContainer width="100%" height={height}>
          <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
            <CartesianGrid
              strokeDasharray="0"
              stroke="var(--color-border)"
              strokeWidth={1}
            />

            <XAxis
              type="number"
              dataKey="x"
              name={xLabel}
              stroke="var(--color-text-faint)"
              tick={{ fill: 'var(--color-text-faint)', fontSize: 10 }}
              axisLine={{ stroke: 'var(--color-border-strong)', strokeWidth: 1 }}
              tickLine={false}
              label={xLabel ? {
                value: xLabel,
                position: 'bottom',
                fill: 'var(--color-text-subtle)',
                fontSize: 11,
                offset: 0,
              } : undefined}
            />

            <YAxis
              type="number"
              dataKey="y"
              name={yLabel}
              stroke="var(--color-text-faint)"
              tick={{ fill: 'var(--color-text-faint)', fontSize: 10 }}
              axisLine={{ stroke: 'var(--color-border-strong)', strokeWidth: 1 }}
              tickLine={false}
              label={yLabel ? {
                value: yLabel,
                angle: -90,
                position: 'insideLeft',
                fill: 'var(--color-text-subtle)',
                fontSize: 11,
              } : undefined}
            />

            <ZAxis type="number" dataKey="size" range={[40, 160]} />

            <Tooltip content={<CustomTooltip />} />

            <Scatter
              data={points}
              fill={dotColor}
              fillOpacity={0.7}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default ScatterPlot;
